import { AnimatePresence, motion } from 'framer-motion';
import { Children, type ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Viewport = styled(motion.div)`
	position: relative;
	overflow: hidden;
`;

const Slide = styled(motion.div)`
	position: absolute;
	inset-inline: 0;
	top: 0;
	width: 100%;
`;

const Measure = styled.div`
	overflow: hidden;
`;

type SlideDirection = 1 | -1;

const slideVariants = {
	enter: (direction: SlideDirection) => ({
		x: direction > 0 ? '100%' : '-100%',
		opacity: 0,
	}),
	center: {
		x: 0,
		opacity: 1,
	},
	exit: (direction: SlideDirection) => ({
		x: direction > 0 ? '-100%' : '100%',
		opacity: 0,
	}),
};

const slideTransition = { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const };
const heightTransition = { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const };

export default function SlideSwitcher(props: { activeIndex: number; children: ReactNode }) {
	const slides = Children.toArray(props.children);
	const lastIndex = Math.max(slides.length - 1, 0);
	const activeIndex = Math.min(Math.max(props.activeIndex, 0), lastIndex);
	const directionRef = useRef<SlideDirection>(1);
	const prevIndexRef = useRef(activeIndex);
	const observerRef = useRef<ResizeObserver | null>(null);
	const [height, setHeight] = useState(0);

	let direction = directionRef.current;
	if (activeIndex !== prevIndexRef.current) {
		direction = activeIndex >= prevIndexRef.current ? 1 : -1;
		directionRef.current = direction;
		prevIndexRef.current = activeIndex;
	}

	useEffect(() => {
		return () => observerRef.current?.disconnect();
	}, []);

	const handleMeasureRef = (node: HTMLDivElement | null) => {
		if (!node) {
			return;
		}

		observerRef.current?.disconnect();
		const updateHeight = () => setHeight(node.getBoundingClientRect().height);
		updateHeight();
		observerRef.current = new ResizeObserver(updateHeight);
		observerRef.current.observe(node);
	};

	const active = slides[activeIndex];
	if (!active) {
		return null;
	}

	return (
		<Viewport animate={{ height: height > 0 ? height : 'auto' }} transition={heightTransition}>
			<AnimatePresence initial={false} custom={direction}>
				<Slide
					key={activeIndex}
					custom={direction}
					variants={slideVariants}
					initial="enter"
					animate="center"
					exit="exit"
					transition={slideTransition}
				>
					<Measure ref={handleMeasureRef}>{active}</Measure>
				</Slide>
			</AnimatePresence>
		</Viewport>
	);
}
